/**
 * External Imports
 */
import {
  FC,
  useEffect,
  useState,
  Dispatch,
  SetStateAction,
  useRef,
  useMemo
} from 'react';
import { connect } from 'react-redux';

/**
 * Nexus Imports
 */
import { NeuNavVertical } from '@neutron/react';
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */
import { logRoundProfileClick } from '../../../utils/analyticsHelpers';

/**
 * Global Type Definition Imports
 */
import { Patient } from '../../../config/interfaces';

/**
 * Style Imports
 */
import { SideNavLabel } from '../SideNav.styles';
import { HistorySideNavVertical } from './HistorySideNav.styles';

interface SideNavProps {
  employeeIssues?: any;
  employeeFeedback?: any;
  employeeRoundHistory?: any;
  patientRoundHistory?: any;
  patientTasks?: any;
  selectedEmployee?: any;
  selectedPatient?: Patient | null;
  selectedTab: string;
  supportType?: string;
  type: string;
  setSelectedTab: Dispatch<SetStateAction<string>>;
}

const HistorySideNav: FC<SideNavProps> = ({
  employeeRoundHistory,
  employeeIssues,
  employeeFeedback,
  patientRoundHistory,
  patientTasks,
  selectedEmployee,
  selectedPatient,
  selectedTab,
  supportType,
  type,
  setSelectedTab
}) => {
  const [numberIssue, setNumberIssue] = useState(0);
  const [numberFeedback, setNumberFeedback] = useState(0);

  const numberOfIssues = useRef<any>(0);
  const numberOfFeedbacks = useRef<any>(0);

  const { logTrackingEvent } = useAnalyticsApi();

  const changeTab = (tab: string) => {
    setSelectedTab(tab);
  };

  const handleClick = (e: any) => {
    switch (e.detail) {
      case 2:
        setSelectedTab('');
        break;
      default:
      // appInsights.trackEvent({
      //   name: 'History_Opened'
      // });
    }
  };

  const hasPastTasks = useMemo(
    () => !(type === 'validation'),
    [type, supportType]
  );

  useEffect(() => {
    numberOfIssues.current = 0;
    numberOfFeedbacks.current = 0;
    if (type === 'patient') {
      patientTasks.map((task: any) => {
        if (task.taskType === 'issue') {
          numberOfIssues.current += 1;
        } else if (task.taskType === 'feedback') {
          numberOfFeedbacks.current += 1;
        }
        return false;
      });
      setNumberIssue(numberOfIssues.current);
      setNumberFeedback(numberOfFeedbacks.current);
    } else {
      numberOfIssues.current += employeeIssues.length;
      numberOfFeedbacks.current += employeeFeedback.length;

      setNumberIssue(numberOfIssues.current);
      setNumberFeedback(numberOfFeedbacks.current);
    }
  }, [patientTasks, employeeIssues, employeeFeedback]);

  return (
    <HistorySideNavVertical
      id="History-Side-Nav-Vertical"
      className="h-auto"
      kind="outlined"
      label="ORBIT HISTORY"
      menu="blue"
      mode="drawer"
    >
      <NeuNavVertical
        unit="header"
        style={{
          padding:
            '0 calc(var(--nav-padding-x) / 1.5) calc(var(--nav-padding-y) / 0.75)'
        }}
      />
      <NeuNavVertical unit="content">
        {!selectedPatient?.mrn?.includes('UL') && (
          <NeuNavVertical
            data-testid="history-side-nav-notes-button"
            className="ps-2"
            active={selectedTab === 'notes'}
            unit="item"
            onClick={e => {
              if (type === 'patient' && selectedPatient?.notes) {
                logTrackingEvent(logRoundProfileClick('past-notes'));
                changeTab('notes');
                handleClick(e);
              }
              if (
                (type === 'employee' ||
                  type === 'validation' ||
                  type === 'support') &&
                selectedEmployee?.notes
              ) {
                // TODO: This is emitting csc need to check helper for csc or csrn and need to handle validation
                logTrackingEvent(
                  logRoundProfileClick('past-notes', supportType)
                );
                changeTab('notes');
                handleClick(e);
              }
            }}
          >
            <SideNavLabel>
              Notes
              {type === 'patient'
                ? selectedPatient?.notes
                  ? ` (${selectedPatient?.notes?.length})`
                  : ' (N/A)'
                : selectedEmployee?.notes
                ? ` (${selectedEmployee?.notes?.length})`
                : ' (N/A)'}
            </SideNavLabel>
          </NeuNavVertical>
        )}
        <NeuNavVertical
          data-testid="history-side-nav-past-rounds-button"
          className="ps-2"
          active={selectedTab === 'pastRounds'}
          unit="item"
          onClick={e => {
            if (type === 'patient') {
              if (patientRoundHistory && patientRoundHistory.length) {
                logTrackingEvent(logRoundProfileClick('past-rounds'));
                changeTab('pastRounds');
                handleClick(e);
              }
            }
            if (
              type === 'employee' ||
              type === 'validation' ||
              type === 'support'
            ) {
              if (employeeRoundHistory && employeeRoundHistory.length) {
                // TODO: This is emitting csc need to check helper for csc or csrn and need to check validation
                logTrackingEvent(
                  logRoundProfileClick('past-rounds', supportType)
                );
                changeTab('pastRounds');
                handleClick(e);
              }
            }
          }}
        >
          <SideNavLabel>
            Past {type === 'validation' ? 'Validations' : 'Rounds'}
            {type === 'patient'
              ? ` (${patientRoundHistory && patientRoundHistory.length})`
              : ` (${employeeRoundHistory && employeeRoundHistory.length})`}
          </SideNavLabel>
        </NeuNavVertical>
        {hasPastTasks && (
          <>
            <NeuNavVertical
              className="ps-2"
              data-testid="history-side-nav-past-issues-button"
              active={selectedTab === 'pastIssues'}
              unit="item"
              onClick={e => {
                if (numberIssue) {
                  logTrackingEvent(logRoundProfileClick('past-issues'));
                  changeTab('pastIssues');
                  handleClick(e);
                }
              }}
            >
              <SideNavLabel>Past Issues{` (${numberIssue})`}</SideNavLabel>
            </NeuNavVertical>

            <NeuNavVertical
              className="ps-2"
              data-testid="history-side-nav-past-feedback-button"
              active={selectedTab === 'pastFeedback'}
              unit="item"
              onClick={e => {
                if (numberFeedback) {
                  logTrackingEvent(logRoundProfileClick('past-feedback'));
                  changeTab('pastFeedback');
                  handleClick(e);
                }
              }}
            >
              <SideNavLabel>Past Feedback{` (${numberFeedback})`}</SideNavLabel>
            </NeuNavVertical>
          </>
        )}
      </NeuNavVertical>
    </HistorySideNavVertical>
  );
};

const mapReduxStateToProps = (state: any) => {
  return {
    employeeIssues: state.TaskReducer.employeeIssues,
    employeeFeedback: state.TaskReducer.employeeFeedback,
    employeeRoundHistory: state.ReportReducer.employeeRoundHistory,
    patientRoundHistory: state.ReportReducer.patientRoundHistory,
    patientTasks: state.TaskReducer.patientTasks
  };
};

export default connect(mapReduxStateToProps)(HistorySideNav);
